import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import api, {
  getClubAthletesWithPayments,
  getProducts,
  getAthleteProducts,
  assignProductToAthlete,
  updateAthleteProduct,
  removeAthleteProduct,
  getClubTeams,
  getClubSettings,
  getPaymentSummary,
  getPaymentHistory,
  type AthleteWithPayments,
  type Product,
  type AthleteProduct,
  type PaymentStatus,
  type PaymentMethod,
  type PaymentSummary,
  type PaymentHistoryItem,
} from "../../services/api";
import { createInvitation } from "../../services/invitations";
import { deleteUser, updateUser, updateAthleteProfile } from "../../services/users";
import ClubAdminLayout from "../../layouts/ClubAdminLayout";
import { ClubNavIcon } from "../../layouts/clubNav";
import { useAgeGroupOptions } from "../../hooks/useAgeGroupOptions";
import AdminPageHeader, { AdminButton } from "../../components/admin/AdminPageHeader";
import { AdminCard, StatCard, StatGrid } from "../../components/admin/AdminCard";
import { formatDate } from "../../lib/dateFormat";
import DateInput from "../../components/DateInput";
import PendingAthletesBanner from "../../components/club/PendingAthletesBanner";

type ColumnKey =
  | "athlete"
  | "email"
  | "team"
  | "sport"
  | "source"
  | "category"
  | "products"
  | "total"
  | "dueDate"
  | "status";

/** How somebody got into the club, in words rather than a code. */
function joinSourceLabel(source?: { kind: string; by_name: string | null; label: string | null } | null): string {
  if (!source) return "—";
  if (source.kind === "invitation") return source.by_name ? `invitat de ${source.by_name}` : "invitat de club";
  if (source.kind === "join_link") {
    const whose = source.by_name ? `linkul lui ${source.by_name}` : "link de înscriere";
    return source.label ? `${whose} (${source.label})` : whose;
  }
  if (source.kind === "added_by_club") {
    return source.by_name ? `adăugat de ${source.by_name}` : "adăugat de club";
  }
  return "—";
}

export default function ClubAthletes() {
  const navigate = useNavigate();
  const { activeClubId } = useAuth();
  const { t } = useTranslation();

  const paymentStatusLabels: Record<PaymentStatus, { label: string; color: string }> = {
    pending: { label: t("clubAthletes.status_pending", "În așteptare"), color: "bg-amber-100 text-amber-700" },
    paid: { label: t("clubAthletes.status_paid", "Plătit"), color: "bg-emerald-100 text-emerald-700" },
    overdue: { label: t("clubAthletes.status_overdue", "Restanță"), color: "bg-rose-100 text-rose-700" },
  };

  const paymentMethodLabels: Record<PaymentMethod, string> = {
    cash: t("clubAthletes.method_cash", "Numerar"),
    card: t("clubAthletes.method_card", "Card"),
    bank_transfer: t("clubAthletes.method_bank_transfer", "Transfer bancar"),
  };

  const recurrenceLabels: Record<string, string> = {
    hourly: t("clubAthletes.recurrence_hourly", "pe oră"),
    weekly: t("clubAthletes.recurrence_weekly", "săptămânal"),
    monthly: t("clubAthletes.recurrence_monthly", "lunar"),
  };

  // ISO weekdays (1 = Monday), matching what the backend expects in `due_day`
  // for weekly products.
  const WEEKDAYS = [
    { value: "1", label: t("common.monday", "Luni") },
    { value: "2", label: t("common.tuesday", "Marți") },
    { value: "3", label: t("common.wednesday", "Miercuri") },
    { value: "4", label: t("common.thursday", "Joi") },
    { value: "5", label: t("common.friday", "Vineri") },
    { value: "6", label: t("common.saturday", "Sâmbătă") },
    { value: "7", label: t("common.sunday", "Duminică") },
  ];

  /** How a stored `due_day` should read for a given recurrence. */
  const dueDayLabel = (recurrence: string, dueDay: number) =>
    recurrence === "weekly"
      ? WEEKDAYS.find((d) => d.value === String(dueDay))?.label || `${t("clubAthletes.day", "Zi")} ${dueDay}`
      : `${t("clubAthletes.day", "Zi")} ${dueDay}`;

  const allColumns: { key: ColumnKey; label: string }[] = [
    { key: "athlete", label: t("clubAthletes.col_athlete", "Sportiv") },
    { key: "email", label: t("clubAthletes.col_email", "Email") },
    { key: "team", label: t("clubAthletes.col_team", "Echipa") },
    { key: "sport", label: t("clubAthletes.col_sport", "Sport") },
    { key: "source", label: t("clubAthletes.col_source", "Cum a venit") },
    { key: "category", label: t("clubAthletes.col_category", "Categorie") },
    { key: "products", label: t("clubAthletes.col_products", "Produse") },
    { key: "total", label: t("clubAthletes.col_total", "Total Datorat") },
    { key: "dueDate", label: t("clubAthletes.col_due_date", "Scadență") },
    { key: "status", label: t("clubAthletes.col_status", "Status") },
  ];

  const getColumnLabel = (key: ColumnKey): string => {
    const col = allColumns.find(c => c.key === key);
    return col ? col.label : key;
  };

  const [athletes, setAthletes] = useState<AthleteWithPayments[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [teams, setTeams] = useState<{ id: string; name: string; age_group?: string | null; age_group_id?: string | null; age_group_ref?: { id: string; name: string; min_age: number | null; max_age: number | null } | null; min_age?: number | null; sport_type?: string | null }[]>([]);
  // Age groups the club itself has configured (may be empty — most clubs never
  // set them up). The picker below merges these with the platform-wide list.
  const [clubAgeGroups, setClubAgeGroups] = useState<string[]>([]);
  const [paymentSummary, setPaymentSummary] = useState<PaymentSummary | null>(null);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [teamFilter, setTeamFilter] = useState("");
  const [ageGroupFilter, setAgeGroupFilter] = useState("");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("");

  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState<ColumnKey[]>([
    "athlete",
    "team",
    "sport",
    "source",
    "category",
    "products",
    "total",
    "status",
  ]);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  // Per-team collections are hidden by default to keep the page compact —
  // the admin expands them on demand from the roster toolbar.
  const [showTeamBreakdown, setShowTeamBreakdown] = useState(false);

  // Athlete Products Modal
  const [selectedAthlete, setSelectedAthlete] = useState<AthleteWithPayments | null>(null);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [athleteProducts, setAthleteProducts] = useState<AthleteProduct[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Bulk selection — `selectedIds` is the source of truth, checkboxes
  // derive from it. Per-row delete uses an inline confirmation chip
  // (no browser confirm dialog) so the workflow can't be silently blocked
  // by ad-blockers.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [pendingBulkDelete, setPendingBulkDelete] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Athlete row-edit modal — full_name / email / phone (users table) +
  // team_id / age_group (athlete_profiles).
  const [editingAthlete, setEditingAthlete] = useState<AthleteWithPayments | null>(null);
  const [editAthleteForm, setEditAthleteForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    team_ids: [] as string[],
    age_group: "",
  });
  const [editAthleteSaving, setEditAthleteSaving] = useState(false);
  const [editAthleteError, setEditAthleteError] = useState("");

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const allSelected = athletes.length > 0 && athletes.every((a) => prev.has(a.id));
      if (allSelected) return new Set();
      return new Set(athletes.map((a) => a.id));
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  /**
   * Let a waiting athlete into the club, from the roster itself.
   *
   * The same decision lives on the club's home page, but this is where an
   * admin actually notices them: they are already listed here, and until this
   * is done nothing about them can be edited.
   */
  const approveMembership = async (athlete: AthleteWithPayments) => {
    const membershipId = athlete.pending_membership?.id;
    if (!membershipId) return;
    setApprovingId(athlete.id);
    try {
      await api.post(`/join-links/requests/${membershipId}/approve`);
      toast.success(t("clubAthletes.approved", "Sportiv acceptat în club."));
      await fetchAthletes();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || t("clubAthletes.approveFailed", "Nu am putut accepta cererea."));
    }
    setApprovingId(null);
  };

  const openEditAthleteModal = (athlete: AthleteWithPayments) => {
    setEditingAthlete(athlete);
    const teamIds = athlete.teams && athlete.teams.length
      ? athlete.teams.map((t) => t.id)
      : athlete.athlete_profiles?.team?.id
        ? [athlete.athlete_profiles.team.id]
        : [];
    setEditAthleteForm({
      full_name: athlete.full_name || "",
      email: athlete.email || "",
      phone: athlete.phone || "",
      team_ids: teamIds,
      age_group: athlete.athlete_profiles?.age_group || "",
    });
    setEditAthleteError("");
  };

  const handleSaveAthleteEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingAthlete) return;
    if (!editAthleteForm.full_name.trim()) {
      setEditAthleteError(t("clubAthletes.err_name_required", "Numele este obligatoriu"));
      return;
    }
    setEditAthleteSaving(true);
    setEditAthleteError("");
    try {
      // 1) users table fields
      const userUpdates: any = {};
      if (editAthleteForm.full_name.trim() !== (editingAthlete.full_name || "")) {
        userUpdates.full_name = editAthleteForm.full_name.trim();
      }
      if (editAthleteForm.email.trim().toLowerCase() !== (editingAthlete.email || "").toLowerCase()) {
        userUpdates.email = editAthleteForm.email.trim();
      }
      const currentPhone = editingAthlete.phone || "";
      if (editAthleteForm.phone.trim() !== currentPhone) {
        userUpdates.phone = editAthleteForm.phone.trim();
      }
      if (Object.keys(userUpdates).length > 0) {
        await updateUser(editingAthlete.id, userUpdates);
      }

      // 2) athlete_profiles fields
      const profileUpdates: any = {};
      const currentTeamIds = (editingAthlete.teams && editingAthlete.teams.length
        ? editingAthlete.teams.map((t) => t.id)
        : editingAthlete.athlete_profiles?.team?.id
          ? [editingAthlete.athlete_profiles.team.id]
          : []
      ).slice().sort();
      const nextTeamIds = editAthleteForm.team_ids.slice().sort();
      if (JSON.stringify(currentTeamIds) !== JSON.stringify(nextTeamIds)) {
        profileUpdates.team_ids = editAthleteForm.team_ids;
      }
      // age_group is no longer edited here (the team owns it), so it's left
      // untouched — sending it would wipe values set before this change.
      if (Object.keys(profileUpdates).length > 0) {
        await updateAthleteProfile(editingAthlete.id, profileUpdates);
      }

      toast.success(t("clubAthletes.athleteUpdated", "Sportiv actualizat"));
      setEditingAthlete(null);
      fetchAthletes();
    } catch (err: any) {
      const msg = err?.response?.data?.error || t("clubAthletes.err_save_failed", "Nu s-a putut salva sportivul");
      setEditAthleteError(msg);
    } finally {
      setEditAthleteSaving(false);
    }
  };

  const handleSingleDelete = async (athleteId: string) => {
    setDeletingId(athleteId);
    try {
      await deleteUser(athleteId);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(athleteId);
        return next;
      });
      toast.success(t("clubAthletes.athleteDeleted", "Sportiv șters"));
      setPendingDeleteId(null);
      fetchAthletes();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || t("clubAthletes.err_delete_failed", "Nu s-a putut șterge sportivul"));
    } finally {
      setDeletingId(null);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkBusy(true);
    const ids = Array.from(selectedIds);
    let ok = 0, fail = 0;
    for (const id of ids) {
      try {
        await deleteUser(id);
        ok++;
      } catch {
        fail++;
      }
    }
    clearSelection();
    setPendingBulkDelete(false);
    setBulkBusy(false);
    if (fail === 0) toast.success(t("clubAthletes.bulkDeleteSuccess", "{{count}} sportivi șterși", { count: ok }));
    else toast.error(t("clubAthletes.bulkDeletePartial", "{{ok}} șterși, {{fail}} eșuați", { ok, fail }));
    fetchAthletes();
  };

  // Add Athlete modal state — tabbed: "manual" (direct create) | "invite" (email link)
  const [showAddModal, setShowAddModal] = useState(false);
  const [addTab, setAddTab] = useState<"manual" | "invite">("manual");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteTeamId, setInviteTeamId] = useState("");
  // Most athletes here are minors and have no email of their own; the club has
  // the parent's. In that case the invitation goes to the parent and carries
  // the child's name, and the parent creates the account into this same team.
  const [inviteTarget, setInviteTarget] = useState<"athlete" | "parent">("athlete");
  const [inviteAthleteName, setInviteAthleteName] = useState("");
  const [inviteLoading, setInviteLoading] = useState(false);

  // Manual create-athlete form state (mirrors the coach app pattern)
  const [manualForm, setManualForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    team_id: "",
    birth_date: "",
    age_group: "",
    has_guardian: false,
    guardian_name: "",
    guardian_email: "",
    guardian_phone: "",
    guardian_type: "" as "" | "mama" | "tata" | "tutore",
  });
  const [manualSaving, setManualSaving] = useState(false);
  const [manualError, setManualError] = useState("");

  // Age derived from birth_date — used to suggest age group + auto-trigger guardian gate
  const manualCalculatedAge = useMemo(() => {
    if (!manualForm.birth_date) return null;
    const birth = new Date(manualForm.birth_date);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  }, [manualForm.birth_date]);

  // Age groups offered in the filters and the add/edit athlete forms. Sourced
  // from the platform-wide list so the club can pick one immediately instead of
  // first having to define its own groups in Settings (which left the selector
  // empty and blocked adding athletes).
  const ageGroups = useAgeGroupOptions(clubAgeGroups);

  // Selected team — used to gate guardian fields by team.min_age (with U-XX fallback)
  const manualSelectedTeam = useMemo(
    () => teams.find(t => t.id === manualForm.team_id) || null,
    [teams, manualForm.team_id]
  );

  // Parse a numeric min-age from the team's age_group label (e.g. "U-12" → 12) as last-resort fallback.
  const teamMinAgeFromGroup = useMemo(() => {
    if (!manualSelectedTeam?.age_group) return null;
    const m = manualSelectedTeam.age_group.match(/(\d+)/);
    return m ? Number(m[1]) : null;
  }, [manualSelectedTeam]);

  // Effective team max age — prefer the structured age_group_ref.max_age
  // (which is set when the team uses a catalogue group like U-12 or
  // Preteen 12-16). Falls through to the numeric override and the
  // parsed legacy label only when no structured group is attached.
  const teamMaxAge = manualSelectedTeam?.age_group_ref?.max_age ?? null;
  const effectiveTeamMinAge =
    teamMaxAge != null ? teamMaxAge : (manualSelectedTeam?.min_age ?? teamMinAgeFromGroup);

  // Guardian is REQUIRED if the athlete (by birth date) OR the team's
  // effective upper bound is under 14.
  const manualGuardianRequired =
    (manualCalculatedAge !== null && manualCalculatedAge < 14) ||
    (effectiveTeamMinAge !== null && effectiveTeamMinAge < 14);

  const manualShowGuardian = manualForm.has_guardian || manualGuardianRequired;

  const handleInviteAthlete = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeClubId || !inviteEmail.trim() || !inviteTeamId) return;
    if (inviteTarget === "parent" && !inviteAthleteName.trim()) return;
    setInviteLoading(true);
    try {
      await createInvitation({
        email: inviteEmail.trim(),
        role_code: inviteTarget,
        club_id: activeClubId,
        team_id: inviteTeamId,
        athlete_name: inviteTarget === "parent" ? inviteAthleteName.trim() : undefined,
      });
      toast.success(t("invite.sent", "Invitație trimisă cu succes!"));
      setInviteEmail("");
      setInviteTeamId("");
      setInviteAthleteName("");
      setShowAddModal(false);
    } catch (error: any) {
      toast.error(error.response?.data?.error || t("invite.error", "Nu s-a putut trimite invitația"));
    }
    setInviteLoading(false);
  };

  const resetManualForm = () => {
    setManualForm({
      full_name: "",
      email: "",
      phone: "",
      team_id: "",
      birth_date: "",
      age_group: "",
      has_guardian: false,
      guardian_name: "",
      guardian_email: "",
      guardian_phone: "",
      guardian_type: "",
    });
    setManualError("");
  };

  const handleManualAddAthlete = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualForm.full_name.trim()) {
      setManualError(t("clubAthletes.err_name_required", "Numele este obligatoriu"));
      return;
    }
    if (!manualForm.email.trim()) {
      setManualError(t("clubAthletes.err_email_required", "Email-ul este obligatoriu"));
      return;
    }
    if (!manualForm.team_id) {
      setManualError(t("clubAthletes.err_team_required", "Echipa este obligatorie"));
      return;
    }
    if (manualShowGuardian) {
      if (!manualForm.guardian_name.trim()) {
        setManualError(t("clubAthletes.err_guardian_name_required", "Numele părintelui este obligatoriu"));
        return;
      }
      if (!manualForm.guardian_email.trim()) {
        setManualError(t("clubAthletes.err_guardian_email_required", "Email-ul părintelui este obligatoriu"));
        return;
      }
      if (!manualForm.guardian_type) {
        setManualError(t("clubAthletes.err_guardian_type_required", "Selectează tipul reprezentantului"));
        return;
      }
    }

    setManualSaving(true);
    setManualError("");
    try {
      const payload: any = {
        full_name: manualForm.full_name.trim(),
        email: manualForm.email.trim(),
        phone: manualForm.phone.trim() || null,
        birth_date: manualForm.birth_date || null,
        // No age_group: it comes from the team the athlete is added to.
        has_guardian: manualShowGuardian,
      };
      if (manualShowGuardian) {
        payload.guardian_contact = {
          full_name: manualForm.guardian_name.trim(),
          email: manualForm.guardian_email.trim(),
          phone: manualForm.guardian_phone.trim() || null,
          relationship: manualForm.guardian_type,
        };
      }
      const { data } = await api.post(`/teams/${manualForm.team_id}/create-athlete`, payload);
      const parentCreated = data?.parent_created === true;
      const parentEmailSent = data?.parent_setup_email_sent === true;
      // Tell the admin what just hit the inbox, so they don't go hunting
      // for an invitation that wasn't sent. Setup email is always queued
      // for the athlete; parent only when we created a fresh parent user.
      toast.success(
        parentCreated && parentEmailSent
          ? t(
              "clubAthletes.athlete_and_parent_created_with_emails",
              "Sportiv adăugat. Email de activare trimis sportivului și părintelui."
            )
          : parentCreated
          ? t(
              "clubAthletes.athlete_and_parent_created",
              "Sportiv adăugat. Email de activare trimis sportivului; părintele are deja cont."
            )
          : t(
              "clubAthletes.athlete_added_with_email",
              "Sportiv adăugat. Email de activare trimis pe {{email}}.",
              { email: manualForm.email.trim() }
            )
      );
      resetManualForm();
      setShowAddModal(false);
      fetchAthletes();
    } catch (err: any) {
      setManualError(err.response?.data?.error || t("clubAthletes.err_add_failed", "Nu s-a putut adăuga sportivul"));
    } finally {
      setManualSaving(false);
    }
  };

  // Add Product Form
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [addProductForm, setAddProductForm] = useState({
    product_id: "",
    due_day: "",
  });
  const [addingProduct, setAddingProduct] = useState(false);
  // Recurrence of the product being assigned — drives whether the scadență
  // field asks for a weekday, a day of the month, or nothing at all.
  const addProductRecurrence = products.find((p) => p.id === addProductForm.product_id)?.recurrence_type;

  // Payment Modal
  const [paymentModal, setPaymentModal] = useState<{
    assignmentId: string;
    productName: string;
    amount: number;
  } | null>(null);
  const [paymentForm, setPaymentForm] = useState<{
    method: PaymentMethod;
    bank_reference: string;
  }>({ method: "cash", bank_reference: "" });
  const [processingPayment, setProcessingPayment] = useState(false);

  // Payment History
  const [showHistory, setShowHistory] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (activeClubId) {
      fetchInitialData();
    }
  }, [activeClubId]);

  useEffect(() => {
    if (activeClubId) {
      fetchAthletes();
    }
  }, [activeClubId, debouncedSearch, teamFilter, ageGroupFilter, paymentStatusFilter]);

  const fetchInitialData = async () => {
    if (!activeClubId) return;
    try {
      const [teamsData, settingsData, productsData, summaryData] = await Promise.all([
        getClubTeams(activeClubId),
        getClubSettings(activeClubId),
        getProducts(activeClubId),
        getPaymentSummary(activeClubId),
      ]);
      setTeams(teamsData);
      setClubAgeGroups(settingsData.age_groups || []);
      setProducts(productsData.filter(p => p.is_active));
      setPaymentSummary(summaryData);
    } catch (error) {
      console.error("Failed to fetch initial data:", error);
    }
  };

  const fetchAthletes = async () => {
    if (!activeClubId) return;
    try {
      setLoading(true);
      const athletesData = await getClubAthletesWithPayments(activeClubId, {
        search: debouncedSearch || undefined,
        team_id: teamFilter || undefined,
        age_group: ageGroupFilter || undefined,
        payment_status: paymentStatusFilter || undefined,
      });
      setAthletes(athletesData);
    } catch (error) {
      console.error("Failed to fetch athletes:", error);
      toast.error(t("clubAthletes.error_load_data", "Nu s-au putut încărca datele"));
    } finally {
      setLoading(false);
    }
  };

  const openAthleteModal = async (athlete: AthleteWithPayments) => {
    setSelectedAthlete(athlete);
    setShowAddProduct(false);
    setShowHistory(false);
    setAddProductForm({ product_id: "", due_day: "" });
    setLoadingProducts(true);

    try {
      const data = await getAthleteProducts(athlete.id);
      setAthleteProducts(data);
    } catch (error) {
      console.error("Failed to fetch athlete products:", error);
      toast.error(t("clubAthletes.error_load_products", "Nu s-au putut încărca produsele sportivului"));
    } finally {
      setLoadingProducts(false);
    }
  };

  const closeModal = () => {
    setSelectedAthlete(null);
    setAthleteProducts([]);
    setShowAddProduct(false);
    setShowHistory(false);
    setPaymentHistory([]);
    fetchAthletes();
    if (activeClubId) {
      getPaymentSummary(activeClubId).then(setPaymentSummary).catch(console.error);
    }
  };

  const handleAddProduct = async () => {
    if (!selectedAthlete || !addProductForm.product_id) {
      toast.error(t("clubAthletes.select_product", "Selectează un produs"));
      return;
    }

    setAddingProduct(true);
    try {
      const newAssignment = await assignProductToAthlete(selectedAthlete.id, {
        product_id: addProductForm.product_id,
        due_day: addProductForm.due_day ? parseInt(addProductForm.due_day) : undefined,
      });
      setAthleteProducts([...athleteProducts, newAssignment]);
      setShowAddProduct(false);
      setAddProductForm({ product_id: "", due_day: "" });
      toast.success(t("clubAthletes.product_added", "Produs adăugat!"));
    } catch (error: any) {
      console.error("Failed to add product:", error);
      toast.error(error.response?.data?.error || t("clubAthletes.error_add_product", "Nu s-a putut adăuga produsul"));
    } finally {
      setAddingProduct(false);
    }
  };

  const openPaymentModal = (ap: AthleteProduct) => {
    setPaymentModal({
      assignmentId: ap.id,
      productName: ap.product.name,
      amount: ap.product.price_cents,
    });
    setPaymentForm({ method: "cash", bank_reference: "" });
  };

  const handleMarkAsPaid = async () => {
    if (!paymentModal) return;

    setProcessingPayment(true);
    try {
      const updated = await updateAthleteProduct(paymentModal.assignmentId, {
        payment_status: "paid",
        payment_method: paymentForm.method,
        bank_reference: paymentForm.method === "bank_transfer" ? paymentForm.bank_reference : undefined,
      });
      setAthleteProducts(athleteProducts.map(ap => ap.id === paymentModal.assignmentId ? updated : ap));
      setPaymentModal(null);
      toast.success(t("clubAthletes.payment_recorded", "Plata înregistrată!"));
    } catch (error: any) {
      console.error("Failed to mark as paid:", error);
      toast.error(error.response?.data?.error || t("clubAthletes.error_record_payment", "Nu s-a putut înregistra plata"));
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleResetPayment = async (assignmentId: string) => {
    try {
      const updated = await updateAthleteProduct(assignmentId, { payment_status: "pending" });
      setAthleteProducts(athleteProducts.map(ap => ap.id === assignmentId ? updated : ap));
      toast.success(t("clubAthletes.status_reset", "Status resetat!"));
    } catch (error: any) {
      console.error("Failed to reset status:", error);
      toast.error(error.response?.data?.error || t("clubAthletes.error_reset_status", "Nu s-a putut reseta statusul"));
    }
  };

  const handleRemoveProduct = async (assignmentId: string) => {
    if (!window.confirm(t("clubAthletes.confirm_remove_product", "Sigur vrei să ștergi acest produs de la sportiv?"))) return;

    try {
      await removeAthleteProduct(assignmentId);
      setAthleteProducts(athleteProducts.filter(ap => ap.id !== assignmentId));
      toast.success(t("clubAthletes.product_removed", "Produs eliminat!"));
    } catch (error: any) {
      console.error("Failed to remove product:", error);
      toast.error(error.response?.data?.error || t("clubAthletes.error_remove_product", "Nu s-a putut elimina produsul"));
    }
  };

  const loadPaymentHistory = async () => {
    if (!selectedAthlete) return;
    setLoadingHistory(true);
    try {
      const history = await getPaymentHistory(selectedAthlete.id);
      setPaymentHistory(history);
      setShowHistory(true);
    } catch (error) {
      console.error("Failed to load history:", error);
      toast.error(t("clubAthletes.error_load_history", "Nu s-a putut încărca istoricul"));
    } finally {
      setLoadingHistory(false);
    }
  };

  const formatPrice = (cents: number, currency: string = "RON") => {
    return new Intl.NumberFormat("ro-RO", {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  };

  const toggleColumn = (key: ColumnKey) => {
    if (visibleColumns.includes(key)) {
      setVisibleColumns(visibleColumns.filter(k => k !== key));
    } else {
      setVisibleColumns([...visibleColumns, key]);
    }
  };

  if (!activeClubId) {
    return (
      <ClubAdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <p className="text-slate-600">{t("clubAthletes.no_active_club", "Nu există club activ selectat")}</p>
        </div>
      </ClubAdminLayout>
    );
  }

  return (
    <ClubAdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <AdminPageHeader
          title={t("clubAthletes.title", "Sportivi")}
          subtitle={`${t("clubAthletes.subtitle", "Gestionează sportivii și plățile acestora")} (${athletes.length})`}
          icon={<ClubNavIcon id="athletes" className="w-6 h-6" />}
          actions={
            <AdminButton
              onClick={() => { setAddTab("manual"); setShowAddModal(true); }}
              icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>}
            >
              {t("clubAthletes.add_athlete", "Adaugă Sportiv")}
            </AdminButton>
          }
        />

        {/* Who is waiting at the door. Above the figures on purpose: an
            unaccepted athlete is on no squad and is billed nothing, so a queue
            nobody notices is the same as no gate at all. The count comes from
            the club rather than the filtered table below, so a search cannot
            make four people waiting look like one. */}
        <PendingAthletesBanner clubId={activeClubId} onChanged={fetchAthletes} />

        {/* KPI tiles */}
        {paymentSummary && (
          <StatGrid>
            <StatCard
              label={t("clubAthletes.collected_this_month", "Încasat luna aceasta")}
              value={formatPrice(paymentSummary.collected_cents)}
              dotClass="bg-emerald-500"
              hint={`${paymentSummary.collected_count} ${t("clubAthletes.payments", "plăți")}`}
              hintTone={paymentSummary.collected_count > 0 ? "up" : "muted"}
            />
            <StatCard
              label={t("clubAthletes.pending", "În așteptare")}
              value={formatPrice(paymentSummary.pending_cents)}
              dotClass="bg-amber-500"
              hint={`${paymentSummary.pending_count} ${t("clubAthletes.payments", "plăți")}`}
              hintTone="muted"
            />
            <StatCard
              label={t("clubAthletes.overdue", "Restanțe")}
              value={formatPrice(paymentSummary.overdue_cents)}
              dotClass="bg-rose-500"
              valueClass={paymentSummary.overdue_cents > 0 ? "text-rose-600" : "text-slate-800"}
              hint={`${paymentSummary.overdue_count} ${t("clubAthletes.athletes_count", "sportivi")}`}
              hintTone={paymentSummary.overdue_count > 0 ? "down" : "muted"}
            />
            <StatCard
              label={t("clubAthletes.total_athletes", "Sportivi")}
              value={athletes.length}
              dotClass="bg-indigo-500"
              hint={`${teams.length} ${t("admin.teams", "Echipe").toLowerCase()}`}
              hintTone="muted"
            />
          </StatGrid>
        )}

        {/* Roster — search, filters, per-team collections and the table all in
            one compact card so the data is reachable without scrolling past
            stacked panels. */}
        <AdminCard>
          {/* Toolbar */}
          <div className="flex flex-col gap-2 p-3 border-b border-slate-100">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              {/* Search */}
              <div className="relative flex-1 min-w-0">
                <svg className="absolute w-4 h-4 -translate-y-1/2 left-3 top-1/2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder={t("clubAthletes.search_placeholder", "Caută după nume sau email...")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full py-2 pl-9 pr-3 text-sm bg-slate-50 border border-slate-200 rounded-lg text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              {/* Filters + toggles */}
              <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:gap-2">
                <select
                  value={teamFilter}
                  onChange={(e) => setTeamFilter(e.target.value)}
                  className="px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 min-w-0"
                >
                  <option value="">{t("clubAthletes.all_teams", "Toate echipele")}</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.id}>{team.name}</option>
                  ))}
                </select>
                <select
                  value={ageGroupFilter}
                  onChange={(e) => setAgeGroupFilter(e.target.value)}
                  className="px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 min-w-0"
                >
                  <option value="">{t("clubAthletes.all_categories", "Toate categoriile")}</option>
                  {ageGroups.map(ag => (
                    <option key={ag} value={ag}>{ag}</option>
                  ))}
                </select>
                <select
                  value={paymentStatusFilter}
                  onChange={(e) => setPaymentStatusFilter(e.target.value)}
                  className="px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 min-w-0"
                >
                  <option value="">{t("clubAthletes.all_statuses", "Toate statusurile")}</option>
                  <option value="pending">{t("clubAthletes.status_pending", "În așteptare")}</option>
                  <option value="paid">{t("clubAthletes.status_up_to_date", "La zi")}</option>
                  <option value="overdue">{t("clubAthletes.status_overdue", "Restanță")}</option>
                </select>
                {paymentSummary && paymentSummary.team_summaries.length > 0 && (
                  <button
                    onClick={() => setShowTeamBreakdown((v) => !v)}
                    className={`px-3 py-2 text-sm rounded-lg border transition-colors flex items-center justify-center gap-1.5 whitespace-nowrap ${showTeamBreakdown ? "bg-indigo-50 border-indigo-200 text-indigo-700" : "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100"}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                    {t("clubAthletes.by_teams_short", "Pe echipe")}
                  </button>
                )}
                {/* Column selector — hidden on mobile (table auto-hides cols) */}
                <div className="relative hidden sm:block">
                  <button
                    onClick={() => setShowColumnSelector(!showColumnSelector)}
                    className="px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg text-slate-700 hover:bg-slate-100 transition-colors flex items-center gap-1.5"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                    </svg>
                    {t("clubAthletes.columns", "Coloane")}
                  </button>
                  {showColumnSelector && (
                    <div className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-lg border border-slate-200 p-3 z-10 min-w-[180px]">
                      {allColumns.map(col => (
                        <label key={col.key} className="flex items-center gap-2 py-1.5 cursor-pointer hover:bg-slate-50 px-2 rounded">
                          <input
                            type="checkbox"
                            checked={visibleColumns.includes(col.key)}
                            onChange={() => toggleColumn(col.key)}
                            className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                          />
                          <span className="text-sm text-slate-700">{col.label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Collapsible per-team collections — a slim horizontal strip
                instead of a full grid block, expanded on demand. */}
            {showTeamBreakdown && paymentSummary && paymentSummary.team_summaries.length > 0 && (
              <div className="flex gap-2 px-1 pt-1 pb-0.5 -mx-1 overflow-x-auto">
                {paymentSummary.team_summaries.map(team => (
                  <div key={team.team_id} className="flex-shrink-0 px-3 py-1.5 border rounded-lg bg-slate-50 border-slate-200">
                    <p className="text-xs font-semibold text-slate-700 whitespace-nowrap">{team.team_name}</p>
                    <div className="flex items-center gap-1.5 text-[11px] whitespace-nowrap">
                      <span className="font-semibold text-emerald-600">{formatPrice(team.collected_cents)}</span>
                      {team.overdue_cents > 0 && (
                        <span className="text-rose-600">· {formatPrice(team.overdue_cents)} {t("clubAthletes.overdue_amount", "restanță")}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Bulk-action toolbar — a band inside the roster card when rows are checked */}
          {selectedIds.size > 0 && (
          <div className="px-3 py-2.5 bg-indigo-50 border-b border-indigo-100 flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-indigo-800">
              {t("clubAthletes.bulkSelected", "{{count}} selectați", { count: selectedIds.size })}
            </span>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              {pendingBulkDelete ? (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-red-100 border border-red-300 rounded-lg">
                  <span className="text-sm text-red-900 font-medium">
                    {t("clubAthletes.bulkDeleteConfirm", "Ștergi {{count}} sportivi?", { count: selectedIds.size })}
                  </span>
                  <button
                    onClick={handleBulkDelete}
                    disabled={bulkBusy}
                    className="px-2.5 py-1 bg-red-700 hover:bg-red-800 text-white rounded text-xs font-medium disabled:opacity-50"
                  >
                    {bulkBusy ? "..." : t("common.yes", "Da")}
                  </button>
                  <button
                    onClick={() => setPendingBulkDelete(false)}
                    disabled={bulkBusy}
                    className="px-2.5 py-1 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 rounded text-xs font-medium disabled:opacity-50"
                  >
                    {t("common.no", "Nu")}
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setPendingBulkDelete(true)}
                  className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium"
                >
                  {t("clubAthletes.bulkDeleteButton", "Șterge selectați")}
                </button>
              )}
              <button
                onClick={clearSelection}
                className="px-3 py-1.5 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-lg text-sm font-medium"
              >
                {t("clubAthletes.bulkClear", "Deselectează")}
              </button>
            </div>
          </div>
        )}

          {/* Body: loading / empty / table */}
          {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 border-2 border-indigo-600 rounded-full border-t-transparent animate-spin"></div>
              <span className="font-medium text-slate-600">{t("common.loading", "Se încarcă...")}</span>
            </div>
          </div>
        ) : athletes.length === 0 ? (
          <div className="p-12 text-center">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-slate-100">
              <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <h3 className="mb-2 text-lg font-semibold text-slate-700">
              {searchTerm || teamFilter || ageGroupFilter ? t("common.no_results", "Niciun rezultat") : t("clubAthletes.no_athletes", "Niciun sportiv")}
            </h3>
            <p className="text-slate-500">
              {searchTerm || teamFilter || ageGroupFilter
                ? t("clubAthletes.try_different_search", "Încearcă să modifici criteriile de căutare")
                : t("clubAthletes.add_first_athlete", "Adaugă primul sportiv din pagina Gestionare Utilizatori")}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b bg-slate-50 border-slate-200">
                  <tr>
                    <th className="px-2 sm:px-4 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={athletes.length > 0 && athletes.every((a) => selectedIds.has(a.id))}
                        ref={(el) => {
                          if (!el) return;
                          const any = athletes.some((a) => selectedIds.has(a.id));
                          const all = athletes.length > 0 && athletes.every((a) => selectedIds.has(a.id));
                          el.indeterminate = any && !all;
                        }}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                      />
                    </th>
                    {visibleColumns.includes("athlete") && (
                      <th className="px-2 sm:px-4 py-3 text-xs font-semibold tracking-wider text-left uppercase text-slate-600">{getColumnLabel("athlete")}</th>
                    )}
                    {visibleColumns.includes("email") && (
                      <th className="hidden md:table-cell px-2 sm:px-4 py-3 text-xs font-semibold tracking-wider text-left uppercase text-slate-600">{getColumnLabel("email")}</th>
                    )}
                    {visibleColumns.includes("team") && (
                      <th className="hidden sm:table-cell px-2 sm:px-4 py-3 text-xs font-semibold tracking-wider text-left uppercase text-slate-600">{getColumnLabel("team")}</th>
                    )}
                    {visibleColumns.includes("category") && (
                      <th className="hidden lg:table-cell px-2 sm:px-4 py-3 text-xs font-semibold tracking-wider text-left uppercase text-slate-600">{getColumnLabel("category")}</th>
                    )}
                    {visibleColumns.includes("products") && (
                      <th className="hidden lg:table-cell px-2 sm:px-4 py-3 text-xs font-semibold tracking-wider text-left uppercase text-slate-600">{getColumnLabel("products")}</th>
                    )}
                    {visibleColumns.includes("total") && (
                      <th className="hidden md:table-cell px-2 sm:px-4 py-3 text-xs font-semibold tracking-wider text-left uppercase text-slate-600">{getColumnLabel("total")}</th>
                    )}
                    {visibleColumns.includes("dueDate") && (
                      <th className="hidden lg:table-cell px-2 sm:px-4 py-3 text-xs font-semibold tracking-wider text-left uppercase text-slate-600">{getColumnLabel("dueDate")}</th>
                    )}
                    {visibleColumns.includes("status") && (
                      <th className="px-2 sm:px-4 py-3 text-xs font-semibold tracking-wider text-left uppercase text-slate-600">{getColumnLabel("status")}</th>
                    )}
                    <th className="px-2 sm:px-4 py-3 text-xs font-semibold tracking-wider text-left uppercase text-slate-600">{t("common.actions", "Acțiuni")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {athletes.map((athlete) => {
                    const isSelected = selectedIds.has(athlete.id);
                    return (
                    <tr
                      key={athlete.id}
                      className={`transition-colors ${isSelected ? "bg-indigo-50" : "hover:bg-slate-50"}`}
                    >
                      <td className="px-2 sm:px-4 py-3 align-top">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(athlete.id)}
                          className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                        />
                      </td>
                      {visibleColumns.includes("athlete") && (
                        <td className="px-2 sm:px-4 py-3 align-top">
                          <div className="flex items-center gap-3 min-w-0">
                            {/* Avatar with initials — the clearest visual cue
                                that this is the redesigned roster table. */}
                            <div className="flex items-center justify-center flex-shrink-0 text-xs font-bold rounded-full w-9 h-9 bg-indigo-100 text-indigo-700">
                              {(athlete.full_name || athlete.email).substring(0, 2).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              {/* The name is the obvious thing to click, so it
                                  opens the athlete's details rather than being
                                  inert text next to a row of small icons. */}
                              <button
                                type="button"
                                onClick={() => openEditAthleteModal(athlete)}
                                className="font-semibold text-left text-slate-800 truncate max-w-[180px] hover:text-indigo-600 hover:underline"
                              >
                                {athlete.full_name || athlete.email.split("@")[0]}
                              </button>
                              {/* On narrow viewports the Team column is hidden, so
                                  surface team + age group under the name. Email is
                                  intentionally NOT shown — it crowds the row and
                                  the user can drill into the profile if needed. */}
                              <p className="sm:hidden text-xs text-slate-500 mt-0.5 truncate max-w-[180px]">
                                {athlete.teams && athlete.teams.length
                                  ? athlete.teams.map((tm) => tm.name).join(", ")
                                  : athlete.athlete_profiles?.team?.name || ""}
                                {athlete.athlete_profiles?.age_group ? ` • ${athlete.athlete_profiles.age_group}` : ""}
                              </p>
                            </div>
                          </div>
                        </td>
                      )}
                      {visibleColumns.includes("email") && (
                        <td className="hidden md:table-cell px-2 sm:px-4 py-3">
                          <p className="text-sm text-slate-500 truncate max-w-[180px]">{athlete.email}</p>
                        </td>
                      )}
                      {visibleColumns.includes("team") && (
                        <td className="hidden sm:table-cell px-2 sm:px-4 py-3">
                          {athlete.teams && athlete.teams.length > 0 ? (
                            <div className="flex flex-wrap gap-1 max-w-[220px]">
                              {athlete.teams.map((tm) => (
                                <span key={tm.id} className="px-2 py-0.5 text-xs font-medium text-slate-700 rounded bg-slate-100 whitespace-nowrap">
                                  {tm.name}
                                </span>
                              ))}
                            </div>
                          ) : athlete.pending_membership?.team_name ? (
                            // Not on the team yet, but they signed up for one —
                            // and that is the thing to know about an athlete
                            // nobody has accepted.
                            <span className="px-2 py-0.5 text-xs font-medium rounded bg-amber-50 text-amber-700 whitespace-nowrap">
                              {athlete.pending_membership.team_name}
                              <span className="ml-1 text-[10px]">(în așteptare)</span>
                            </span>
                          ) : (
                            <span className="text-sm text-slate-700">
                              {athlete.athlete_profiles?.team?.name || "-"}
                            </span>
                          )}
                        </td>
                      )}
                      {visibleColumns.includes("sport") && (
                        <td className="hidden px-2 py-3 lg:table-cell sm:px-4">
                          {athlete.sports && athlete.sports.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {athlete.sports.map((sp) => (
                                <span
                                  key={sp.name}
                                  className="px-2 py-0.5 text-xs font-medium rounded bg-sky-50 text-sky-700 whitespace-nowrap"
                                >
                                  {sp.name}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-sm text-slate-400">-</span>
                          )}
                        </td>
                      )}
                      {visibleColumns.includes("source") && (
                        <td className="hidden px-2 py-3 lg:table-cell sm:px-4">
                          <span className="text-xs text-slate-600">{joinSourceLabel(athlete.join_source)}</span>
                        </td>
                      )}
                      {visibleColumns.includes("category") && (
                        <td className="hidden lg:table-cell px-2 sm:px-4 py-3">
                          {athlete.athlete_profiles?.age_group ? (
                            <span className="px-2 py-1 text-xs font-medium text-indigo-700 rounded bg-indigo-50">
                              {athlete.athlete_profiles.age_group}
                            </span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                      )}
                      {visibleColumns.includes("products") && (
                        <td className="hidden lg:table-cell px-2 sm:px-4 py-3">
                          <span className="text-sm text-slate-700">{athlete.payment_summary.products_count}</span>
                        </td>
                      )}
                      {visibleColumns.includes("total") && (
                        <td className="hidden md:table-cell px-2 sm:px-4 py-3">
                          <span className={`font-semibold text-sm whitespace-nowrap ${
                            athlete.payment_summary.total_due_cents > 0 ? "text-rose-600" : "text-slate-400"
                          }`}>
                            {athlete.payment_summary.total_due_cents > 0
                              ? formatPrice(athlete.payment_summary.total_due_cents)
                              : "-"}
                          </span>
                        </td>
                      )}
                      {visibleColumns.includes("dueDate") && (
                        <td className="hidden lg:table-cell px-2 sm:px-4 py-3">
                          <span className="text-sm text-slate-700 whitespace-nowrap">
                            {formatDate(athlete.payment_summary.next_due_date)}
                          </span>
                        </td>
                      )}
                      {visibleColumns.includes("status") && (
                        <td className="px-2 sm:px-4 py-3">
                          {/* Waiting to be let in comes first: it is a fact
                              about the person, not about their fees, and it
                              blocks everything else. Then the payment state —
                              and "no services yet" rather than a bare dash,
                              which said nothing at all. */}
                          {athlete.pending_membership ? (
                            <span className="px-2 py-1 text-xs font-medium rounded bg-indigo-100 text-indigo-700 whitespace-nowrap">
                              {t("clubAthletes.status_awaiting", "Așteaptă acceptul")}
                            </span>
                          ) : athlete.payment_summary.has_overdue ? (
                            <span className="px-2 py-1 text-xs font-medium text-rose-700 bg-rose-100 rounded whitespace-nowrap">{t("clubAthletes.status_overdue", "Restanță")}</span>
                          ) : athlete.payment_summary.has_pending ? (
                            <span className="px-2 py-1 text-xs font-medium text-amber-700 bg-amber-100 rounded whitespace-nowrap">{t("clubAthletes.status_pending", "În așteptare")}</span>
                          ) : athlete.payment_summary.products_count > 0 ? (
                            <span className="px-2 py-1 text-xs font-medium rounded bg-emerald-100 text-emerald-700 whitespace-nowrap">{t("clubAthletes.status_up_to_date", "La zi")}</span>
                          ) : (
                            <span className="text-xs text-slate-400 whitespace-nowrap">{t("clubAthletes.status_no_services", "Fără servicii")}</span>
                          )}
                        </td>
                      )}
                      <td className="px-2 sm:px-4 py-3">
                        <div className="flex items-center gap-1">
                          {athlete.pending_membership && (
                            <button
                              onClick={() => approveMembership(athlete)}
                              disabled={approvingId === athlete.id}
                              className="px-2.5 py-1.5 text-xs font-bold text-white rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 whitespace-nowrap"
                              title={t("clubAthletes.approveHint", "Acceptă cererea de înscriere")}
                            >
                              {approvingId === athlete.id ? "..." : t("clubAthletes.approve", "Acceptă")}
                            </button>
                          )}
                          <button
                            onClick={() => openAthleteModal(athlete)}
                            className="p-2 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 rounded-lg transition-colors"
                            title={t("clubAthletes.manage", "Gestionează")}
                            aria-label={t("clubAthletes.manage", "Gestionează")}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => openEditAthleteModal(athlete)}
                            className="p-2 bg-slate-50 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                            title={t("clubAthletes.editAthlete", "Editează detalii")}
                            aria-label={t("clubAthletes.editAthlete", "Editează detalii")}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          {pendingDeleteId === athlete.id ? (
                            <div className="flex items-center gap-1.5 px-2 py-1 bg-red-50 border border-red-200 rounded-lg">
                              <span className="text-xs text-red-800 font-medium whitespace-nowrap">
                                {t("clubAthletes.deleteConfirm", "Ștergi?")}
                              </span>
                              <button
                                onClick={() => handleSingleDelete(athlete.id)}
                                disabled={deletingId === athlete.id}
                                className="px-2 py-0.5 bg-red-600 hover:bg-red-700 text-white rounded text-xs font-medium disabled:opacity-50"
                              >
                                {deletingId === athlete.id ? "..." : t("common.yes", "Da")}
                              </button>
                              <button
                                onClick={() => setPendingDeleteId(null)}
                                disabled={deletingId === athlete.id}
                                className="px-2 py-0.5 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 rounded text-xs font-medium disabled:opacity-50"
                              >
                                {t("common.no", "Nu")}
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setPendingDeleteId(athlete.id)}
                              className="p-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                              title={t("clubAthletes.deleteAthlete", "Șterge sportiv")}
                              aria-label={t("clubAthletes.deleteAthlete", "Șterge sportiv")}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
          </div>
          )}
        </AdminCard>

        {/* Athlete Products Modal */}
        {selectedAthlete && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
            <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
              <div className="flex-shrink-0 flex items-center justify-between px-5 py-4 border-b border-slate-200">
                <div>
                  <h2 className="text-lg font-bold text-slate-800">
                    {selectedAthlete.full_name || selectedAthlete.email}
                  </h2>
                  <p className="text-sm text-slate-500">{selectedAthlete.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={loadPaymentHistory}
                    disabled={loadingHistory}
                    className="px-3 py-1.5 bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-lg text-sm font-medium transition-colors flex items-center gap-1"
                  >
                    {loadingHistory ? (
                      <div className="w-4 h-4 border-2 rounded-full border-slate-600 border-t-transparent animate-spin"></div>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                    {t("clubAthletes.history", "Istoric")}
                  </button>
                  <button
                    onClick={closeModal}
                    className="p-2 -mr-2 rounded-lg text-slate-500 hover:bg-slate-100"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="flex-1 p-5 space-y-4 overflow-y-auto">
                {/* Who to actually ring.
                    For a child the useful number is the parent's, and for an
                    athlete nobody has accepted yet it is the only one there
                    is — the child's own address is often synthetic. */}
                <div className="p-4 border rounded-xl border-slate-200 bg-slate-50">
                  <h3 className="mb-2 text-xs font-bold tracking-wide uppercase text-slate-400">
                    {t("clubAthletes.contact", "Contact")}
                  </h3>
                  <dl className="grid gap-3 text-sm sm:grid-cols-2">
                    <div>
                      <dt className="text-slate-500">{t("clubAthletes.athlete", "Sportiv")}</dt>
                      <dd className="text-slate-800">
                        {selectedAthlete.phone ? (
                          <a href={`tel:${selectedAthlete.phone}`} className="text-indigo-600 hover:underline">
                            {selectedAthlete.phone}
                          </a>
                        ) : (
                          <span className="text-slate-400">fără telefon</span>
                        )}
                        <span className="block text-xs break-all text-slate-400">{selectedAthlete.email}</span>
                      </dd>
                    </div>
                    <div>
                      <dt className="text-slate-500">{t("clubAthletes.parents", "Părinți")}</dt>
                      <dd>
                        {selectedAthlete.parents && selectedAthlete.parents.length > 0 ? (
                          <ul className="space-y-1">
                            {selectedAthlete.parents.map((p) => (
                              <li key={p.id} className="text-slate-800">
                                {p.full_name || p.email}
                                {p.phone ? (
                                  <a href={`tel:${p.phone}`} className="ml-1 text-indigo-600 hover:underline">
                                    {p.phone}
                                  </a>
                                ) : (
                                  <span className="ml-1 text-xs text-amber-600">fără telefon</span>
                                )}
                                <span className="block text-xs break-all text-slate-400">{p.email}</span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <span className="text-slate-400">niciun părinte legat de cont</span>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-slate-500">{t("clubAthletes.col_team", "Echipa")}</dt>
                      <dd className="text-slate-800">
                        {selectedAthlete.teams && selectedAthlete.teams.length > 0
                          ? selectedAthlete.teams.map((tm) => tm.name).join(", ")
                          : selectedAthlete.pending_membership?.team_name
                            ? `${selectedAthlete.pending_membership.team_name} (în așteptare)`
                            : "—"}
                        {selectedAthlete.sports && selectedAthlete.sports.length > 0 && (
                          <span className="block text-xs text-slate-400">
                            {selectedAthlete.sports.map((sp) => sp.name).join(", ")}
                          </span>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-slate-500">{t("clubAthletes.col_source", "Cum a venit")}</dt>
                      <dd className="text-slate-800">{joinSourceLabel(selectedAthlete.join_source)}</dd>
                    </div>
                  </dl>
                  <div className="mt-3">
                    <button
                      onClick={() => navigate(`/dashboard/admin-club/athletes/${selectedAthlete.id}`)}
                      className="text-sm font-semibold text-indigo-600 hover:text-indigo-700"
                    >
                      {t("clubAthletes.open360", "Vezi fișa completă a sportivului →")}
                    </button>
                  </div>
                </div>

                {/* Payment History Panel */}
                {showHistory && (
                  <div className="p-4 mb-6 border bg-slate-50 rounded-xl border-slate-200">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-medium text-slate-800">{t("clubAthletes.payment_history", "Istoric plăți")}</h3>
                      <button
                        onClick={() => setShowHistory(false)}
                        className="text-sm text-slate-500 hover:text-slate-700"
                      >
                        {t("common.close", "Închide")}
                      </button>
                    </div>
                    {paymentHistory.length === 0 ? (
                      <p className="py-4 text-sm text-center text-slate-500">{t("clubAthletes.no_payments", "Nicio plată înregistrată")}</p>
                    ) : (
                      <div className="space-y-2 max-h-[200px] overflow-y-auto">
                        {paymentHistory.map(item => (
                          <div key={item.id} className="p-3 text-sm bg-white border rounded-lg border-slate-100">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-slate-800">{item.athlete_product.product.name}</span>
                              <span className="font-semibold text-emerald-600">{formatPrice(item.amount_cents, item.currency)}</span>
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                              <span>{formatDate(item.paid_at)}</span>
                              <span className="px-1.5 py-0.5 bg-slate-100 rounded">{paymentMethodLabels[item.payment_method]}</span>
                              {item.bank_reference && <span>Ref: {item.bank_reference}</span>}
                            </div>
                            <div className="mt-1 text-xs text-slate-500">
                              {t("clubAthletes.collected_by", "Încasat de:")} {item.collected_by_coach
                                ? <span className="font-medium text-slate-700">{item.collected_by_coach.full_name || item.collected_by_coach.email}</span>
                                : <span className="text-slate-600">{t("clubAthletes.club_admin", "Admin Club")}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Add Product Button/Form */}
                {!showAddProduct ? (
                  <button
                    onClick={() => setShowAddProduct(true)}
                    className="flex items-center justify-center w-full gap-2 px-4 py-3 mb-6 font-medium transition-colors border-2 border-dashed border-slate-300 hover:border-indigo-400 rounded-xl text-slate-600 hover:text-indigo-600"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    {t("clubAthletes.add_product_service", "Adaugă Produs/Serviciu")}
                  </button>
                ) : (
                  <div className="p-4 mb-6 border bg-slate-50 rounded-xl border-slate-200">
                    <h4 className="mb-3 font-medium text-slate-800">{t("clubAthletes.add_product", "Adaugă Produs")}</h4>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block mb-2 text-sm font-medium text-slate-700">{t("clubAthletes.product_required", "Produs *")}</label>
                        <select
                          value={addProductForm.product_id}
                          // Reset the day when the product changes — a weekday
                          // value (1-7) means something different on a monthly
                          // product, so carrying it over would be wrong.
                          onChange={(e) => setAddProductForm({ product_id: e.target.value, due_day: "" })}
                          className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        >
                          <option value="">{t("common.select", "Selectează...")}</option>
                          {products.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name} - {formatPrice(p.price_cents)} {recurrenceLabels[p.recurrence_type]}
                            </option>
                          ))}

                        </select>
                      </div>
                      {/* Scadența urmează recurența produsului: la abonamentele
                          săptămânale se alege ziua din săptămână, la cele lunare
                          ziua din lună. Produsele „pe oră" nu au scadență. */}
                      {addProductRecurrence === "weekly" ? (
                        <div>
                          <label className="block mb-2 text-sm font-medium text-slate-700">{t("clubAthletes.due_weekday", "Zi din săptămână *")}</label>
                          <select
                            value={addProductForm.due_day}
                            onChange={(e) => setAddProductForm({ ...addProductForm, due_day: e.target.value })}
                            className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          >
                            <option value="">{t("common.select", "Selectează...")}</option>
                            {WEEKDAYS.map((d) => (
                              <option key={d.value} value={d.value}>{d.label}</option>
                            ))}
                          </select>
                        </div>
                      ) : addProductRecurrence === "hourly" ? (
                        <div className="flex items-end">
                          <p className="text-xs text-slate-500 pb-2">{t("clubAthletes.no_due_day", "Produsele pe oră nu au zi de scadență.")}</p>
                        </div>
                      ) : (
                        <div>
                          <label className="block mb-2 text-sm font-medium text-slate-700">{t("clubAthletes.due_day", "Zi scadență (1-31) *")}</label>
                          <input
                            type="number"
                            min="1"
                            max="31"
                            required
                            value={addProductForm.due_day}
                            onChange={(e) => setAddProductForm({ ...addProductForm, due_day: e.target.value })}
                            placeholder="ex: 15"
                            className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          />
                        </div>
                      )}
                    </div>
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => {
                          setShowAddProduct(false);
                          setAddProductForm({ product_id: "", due_day: "" });
                        }}
                        className="px-3 py-1.5 text-slate-600 hover:text-slate-800 font-medium transition-colors"
                      >
                        {t("common.cancel", "Anulează")}
                      </button>
                      <button
                        onClick={handleAddProduct}
                        disabled={addingProduct || !addProductForm.product_id || (addProductRecurrence !== "hourly" && !addProductForm.due_day)}
                        className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                      >
                        {addingProduct ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white rounded-full border-t-transparent animate-spin"></div>
                            {t("clubAthletes.adding", "Se adaugă...")}
                          </>
                        ) : (
                          t("common.add", "Adaugă")
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {/* Products List */}
                {loadingProducts ? (
                  <div className="py-8 text-center">
                    <div className="w-6 h-6 mx-auto border-2 border-indigo-600 rounded-full border-t-transparent animate-spin"></div>
                    <p className="mt-2 text-slate-500">{t("clubAthletes.loading_products", "Se încarcă produsele...")}</p>
                  </div>
                ) : athleteProducts.length === 0 ? (
                  <div className="py-8 text-center text-slate-500">
                    <p>{t("clubAthletes.no_assigned_products", "Niciun produs/serviciu asignat")}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {athleteProducts.map((ap) => (
                      <div
                        key={ap.id}
                        className="p-4 transition-shadow bg-white border border-slate-200 rounded-xl hover:shadow-sm"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium text-slate-800">{ap.product.name}</h4>
                              <span className={`px-2 py-0.5 text-xs font-medium rounded ${paymentStatusLabels[ap.payment_status].color}`}>
                                {paymentStatusLabels[ap.payment_status].label}
                              </span>
                            </div>
                            <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
                              <span className="font-semibold text-slate-700">
                                {formatPrice(ap.product.price_cents, ap.product.currency)}
                              </span>
                              <span>{recurrenceLabels[ap.product.recurrence_type]}</span>
                              {ap.due_day && <span>{dueDayLabel(ap.product.recurrence_type, ap.due_day)}</span>}
                              {ap.next_due_date && <span>{t("clubAthletes.due_on", "Scadent:")} {formatDate(ap.next_due_date)}</span>}
                              {ap.payment_method && (
                                <span className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">
                                  {paymentMethodLabels[ap.payment_method]}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {ap.payment_status !== "paid" && (
                              <button
                                onClick={() => openPaymentModal(ap)}
                                className="px-3 py-1 text-sm font-medium transition-colors rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
                              >
                                {t("clubAthletes.mark_paid", "Marchează plătit")}
                              </button>
                            )}
                            {ap.payment_status === "paid" && (
                              <button
                                onClick={() => handleResetPayment(ap.id)}
                                className="px-3 py-1 text-sm font-medium transition-colors rounded-lg bg-slate-50 text-slate-600 hover:bg-slate-100"
                              >
                                {t("clubAthletes.reset", "Resetează")}
                              </button>
                            )}
                            <button
                              onClick={() => handleRemoveProduct(ap.id)}
                              className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Summary */}
                {athleteProducts.length > 0 && (
                  <div className="pt-6 mt-6 border-t border-slate-200">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-slate-600">{t("clubAthletes.total_due", "Total de plată:")}</span>
                      <span className="text-xl font-bold text-slate-800">
                        {formatPrice(
                          athleteProducts
                            .filter(ap => ap.payment_status !== "paid")
                            .reduce((sum, ap) => sum + ap.product.price_cents, 0)
                        )}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Payment Method Modal */}
        {paymentModal && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
            <div className="w-full max-w-md bg-white rounded-2xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
              <div className="flex-shrink-0 flex items-center justify-between px-5 py-4 border-b border-slate-200">
                <h3 className="text-lg font-bold text-slate-800">{t("clubAthletes.record_payment", "Înregistrează plata")}</h3>
                <button
                  onClick={() => setPaymentModal(null)}
                  className="p-2 -mr-2 rounded-lg text-slate-500 hover:bg-slate-100"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="flex-1 p-5 space-y-4 overflow-y-auto">
                <p className="text-slate-600">
                  {paymentModal.productName} - <strong>{formatPrice(paymentModal.amount)}</strong>
                </p>

                <div>
                  <label className="block mb-2 text-sm font-medium text-slate-700">{t("clubAthletes.payment_method", "Metoda de plată")}</label>
                  <div className="space-y-2">
                    {(["cash", "card", "bank_transfer"] as PaymentMethod[]).map(method => (
                      <label key={method} className="flex items-center gap-3 p-3 transition-colors rounded-lg cursor-pointer bg-slate-50 hover:bg-slate-100">
                        <input
                          type="radio"
                          name="payment_method"
                          value={method}
                          checked={paymentForm.method === method}
                          onChange={(e) => setPaymentForm({ ...paymentForm, method: e.target.value as PaymentMethod })}
                          className="w-4 h-4 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="text-slate-700">{paymentMethodLabels[method]}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {paymentForm.method === "bank_transfer" && (
                  <div>
                    <label className="block mb-2 text-sm font-medium text-slate-700">
                      {t("clubAthletes.bank_reference", "Număr referință (opțional)")}
                    </label>
                    <input
                      type="text"
                      value={paymentForm.bank_reference}
                      onChange={(e) => setPaymentForm({ ...paymentForm, bank_reference: e.target.value })}
                      placeholder="ex: ORD123456"
                      className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-slate-100">
                <button
                  onClick={() => setPaymentModal(null)}
                  className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium text-sm"
                >
                  {t("common.cancel", "Anulează")}
                </button>
                <button
                  onClick={handleMarkAsPaid}
                  disabled={processingPayment}
                  className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium text-sm disabled:opacity-50"
                >
                  {processingPayment ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white rounded-full border-t-transparent animate-spin"></div>
                      {t("clubAthletes.processing", "Se procesează...")}
                    </>
                  ) : (
                    t("clubAthletes.confirm_payment", "Confirmă plata")
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Click outside to close column selector */}
      {showColumnSelector && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setShowColumnSelector(false)}
        />
      )}

      {/* Add Athlete Modal (tabbed: Manual / Invite) */}
      {showAddModal && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4 bg-black/50" onClick={() => setShowAddModal(false)}>
          <div
            className="w-full max-w-lg bg-white rounded-2xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex-shrink-0 flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <h3 className="text-lg font-bold text-slate-800">{t("clubAthletes.add_athlete", "Adaugă Sportiv")}</h3>
              <button onClick={() => setShowAddModal(false)} className="p-2 -mr-2 rounded-lg text-slate-500 hover:bg-slate-100">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Tabs */}
            <div className="flex-shrink-0 flex border-b border-slate-100 px-5">
              <button
                type="button"
                onClick={() => setAddTab("manual")}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  addTab === "manual"
                    ? "border-indigo-500 text-indigo-600"
                    : "border-transparent text-slate-500 hover:text-slate-700"
                }`}
              >
                {t("clubAthletes.tab_manual", "Adaugă manual")}
              </button>
              <button
                type="button"
                onClick={() => setAddTab("invite")}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  addTab === "invite"
                    ? "border-indigo-500 text-indigo-600"
                    : "border-transparent text-slate-500 hover:text-slate-700"
                }`}
              >
                {t("clubAthletes.tab_invite", "Invită prin email")}
              </button>
            </div>

            {/* Tab content */}
            <div className="flex-1 p-5 space-y-4 overflow-y-auto">
              {addTab === "manual" ? (
                <form id="club-add-athlete-form" onSubmit={handleManualAddAthlete} className="space-y-4">
                  {manualError && (
                    <div className="p-3 bg-red-50 text-red-600 rounded-xl text-sm">{manualError}</div>
                  )}

                  <div>
                    <label className="block mb-2 text-sm font-medium text-slate-700">
                      {t("clubAthletes.full_name", "Nume complet")} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={manualForm.full_name}
                      onChange={e => setManualForm({ ...manualForm, full_name: e.target.value })}
                      placeholder="ex: Ion Popescu"
                      className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium text-slate-700">
                      {t("common.email", "Email")} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={manualForm.email}
                      onChange={e => setManualForm({ ...manualForm, email: e.target.value })}
                      placeholder="sportiv@email.com"
                      className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium text-slate-700">{t("clubAthletes.phone", "Telefon")}</label>
                    <input
                      type="tel"
                      value={manualForm.phone}
                      onChange={e => setManualForm({ ...manualForm, phone: e.target.value })}
                      placeholder="ex: 0722 123 456"
                      className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium text-slate-700">
                      {t("clubAthletes.team", "Echipă")} <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={manualForm.team_id}
                      onChange={e => setManualForm({ ...manualForm, team_id: e.target.value })}
                      className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    >
                      <option value="">{t("clubAthletes.select_team", "Selectează echipa")}</option>
                      {teams.map(team => {
                        const parts: string[] = [team.name];
                        if (team.sport_type) parts.push(team.sport_type);
                        if (team.age_group) parts.push(team.age_group);
                        return (
                          <option key={team.id} value={team.id}>{parts.join(" — ")}</option>
                        );
                      })}
                    </select>
                    {manualSelectedTeam && effectiveTeamMinAge !== null && (
                      <p className="mt-1 text-xs text-slate-500">
                        {t("clubAthletes.team_min_age", "Vârstă minimă echipă")}: {effectiveTeamMinAge}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium text-slate-700">{t("clubAthletes.birth_date", "Data nașterii")}</label>
                    <DateInput
                      value={manualForm.birth_date}
                      onChange={(v) => setManualForm({ ...manualForm, birth_date: v })}
                      max={new Date().toISOString().split("T")[0]}
                      className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    {manualCalculatedAge !== null && (
                      <p className="text-xs text-slate-500 mt-1">
                        {t("clubAthletes.age", "Vârstă")}: {manualCalculatedAge} {t("clubAthletes.years", "ani")}
                      </p>
                    )}
                  </div>

                  {/* Grupa de vârstă nu se mai cere aici: e definită pe echipă,
                      iar sportivul o moștenește din echipa selectată. */}

                  {/* Guardian toggle */}
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="manual_has_guardian"
                      checked={manualForm.has_guardian || manualGuardianRequired}
                      disabled={manualGuardianRequired}
                      onChange={e => setManualForm({ ...manualForm, has_guardian: e.target.checked })}
                      className="mt-1 w-5 h-5 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
                    />
                    <div>
                      <label htmlFor="manual_has_guardian" className={`text-sm font-medium ${manualGuardianRequired ? "text-amber-700" : "text-slate-700"}`}>
                        {t("clubAthletes.has_guardian", "Sportivul are părinte/tutore reprezentant")}
                      </label>
                      {manualGuardianRequired && (
                        <p className="text-xs text-amber-600 mt-0.5">
                          {t("clubAthletes.guardian_required", "Obligatoriu — echipa sau vârsta sportivului este sub 14 ani")}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Guardian fields (gated) */}
                  {manualShowGuardian && (
                    <div className="space-y-3 p-4 bg-amber-50 rounded-xl border border-amber-200">
                      <h4 className="text-sm font-medium text-amber-800">{t("clubAthletes.guardian_info", "Informații părinte/tutore")}</h4>

                      <div>
                        <label className="block mb-2 text-sm font-medium text-slate-700">
                          {t("clubAthletes.guardian_name", "Nume părinte/tutore")} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={manualForm.guardian_name}
                          onChange={e => setManualForm({ ...manualForm, guardian_name: e.target.value })}
                          placeholder="ex: Maria Popescu"
                          className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        />
                      </div>

                      <div>
                        <label className="block mb-2 text-sm font-medium text-slate-700">
                          {t("clubAthletes.guardian_email", "Email părinte/tutore")} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          value={manualForm.guardian_email}
                          onChange={e => setManualForm({ ...manualForm, guardian_email: e.target.value })}
                          placeholder="parinte@email.com"
                          className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        />
                        <p className="text-xs text-amber-700 mt-1">
                          {t("clubAthletes.guardian_account_note", "Pe acest email se va crea un cont de părinte (sau se reutilizează contul existent).")}
                        </p>
                      </div>

                      <div>
                        <label className="block mb-2 text-sm font-medium text-slate-700">{t("clubAthletes.guardian_phone", "Telefon părinte")}</label>
                        <input
                          type="tel"
                          value={manualForm.guardian_phone}
                          onChange={e => setManualForm({ ...manualForm, guardian_phone: e.target.value })}
                          placeholder="ex: 0722 123 456"
                          className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        />
                      </div>

                      <div>
                        <label className="block mb-2 text-sm font-medium text-slate-700">
                          {t("clubAthletes.guardian_type", "Tip reprezentant")} <span className="text-red-500">*</span>
                        </label>
                        <div className="flex gap-2">
                          {[
                            { value: "mama", label: t("clubAthletes.mother", "Mamă") },
                            { value: "tata", label: t("clubAthletes.father", "Tată") },
                            { value: "tutore", label: t("clubAthletes.guardian_legal", "Tutore") },
                          ].map(opt => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => setManualForm({ ...manualForm, guardian_type: opt.value as any })}
                              className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-medium transition-all ${
                                manualForm.guardian_type === opt.value
                                  ? "bg-amber-500 text-white shadow-sm"
                                  : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
                              }`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="bg-indigo-50 rounded-xl p-3 text-xs text-indigo-700">
                    {t("clubAthletes.manual_info", "Se va crea un cont pentru sportiv cu parolă generată automat. Sportivul își poate seta parola ulterior.")}
                  </div>
                </form>
              ) : (
                <form id="club-invite-athlete-form" onSubmit={handleInviteAthlete} className="space-y-4">
                  <div>
                    <label className="block mb-2 text-sm font-medium text-slate-700">
                      {t("invite.who", "Cui trimiți invitația?")}
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {([
                        { key: "athlete", label: t("invite.to_athlete", "Sportivului"), hint: t("invite.to_athlete_hint", "Are email propriu") },
                        { key: "parent", label: t("invite.to_parent", "Părintelui"), hint: t("invite.to_parent_hint", "Nu am emailul copilului") },
                      ] as const).map((opt) => (
                        <button
                          key={opt.key}
                          type="button"
                          onClick={() => setInviteTarget(opt.key)}
                          className={`px-3 py-2.5 text-left rounded-xl border text-sm transition ${
                            inviteTarget === opt.key
                              ? "bg-indigo-50 border-indigo-400 text-indigo-800"
                              : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"
                          }`}
                        >
                          <span className="block font-semibold">{opt.label}</span>
                          <span className="block text-xs opacity-70">{opt.hint}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {inviteTarget === "parent" && (
                    <div>
                      <label className="block mb-2 text-sm font-medium text-slate-700">
                        {t("invite.child_name", "Numele copilului")} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={inviteAthleteName}
                        onChange={(e) => setInviteAthleteName(e.target.value)}
                        placeholder="Andrei Popescu"
                        className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                      <p className="mt-1 text-xs text-slate-500">
                        {t("invite.child_name_hint", "Apare în email, ca părintele să știe despre ce copil e vorba.")}
                      </p>
                    </div>
                  )}

                  <div>
                    <label className="block mb-2 text-sm font-medium text-slate-700">
                      {inviteTarget === "parent"
                        ? t("invite.parent_email", "Emailul părintelui")
                        : t("common.email", "Email")}
                    </label>
                    <input
                      type="email"
                      required
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder={inviteTarget === "parent" ? "parinte@example.com" : "sportiv@example.com"}
                      className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block mb-2 text-sm font-medium text-slate-700">
                      {t("invite.team", "Echipă")} <span className="text-red-500">*</span>
                    </label>
                    <select
                      required
                      value={inviteTeamId}
                      onChange={(e) => setInviteTeamId(e.target.value)}
                      className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="">{t("invite.select_team", "Selectează o echipă")}</option>
                      {teams.map(team => {
                        const parts: string[] = [team.name];
                        if (team.sport_type) parts.push(team.sport_type);
                        if (team.age_group) parts.push(team.age_group);
                        return (
                          <option key={team.id} value={team.id}>{parts.join(" — ")}</option>
                        );
                      })}
                    </select>
                    <p className="mt-1 text-xs text-slate-500">
                      {inviteTarget === "parent"
                        ? t("invite.parent_team_hint", "Copilul intră automat în această echipă când părintele îi creează contul.")
                        : t("invite.athlete_team_hint", "Sportivul va fi alocat automat antrenorului echipei selectate.")}
                    </p>
                  </div>
                </form>
              )}
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 flex items-center justify-end gap-3 px-5 py-4 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium text-sm"
              >
                {t("common.cancel", "Anulează")}
              </button>
              {addTab === "manual" ? (
                <button
                  type="submit"
                  form="club-add-athlete-form"
                  disabled={manualSaving}
                  className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium text-sm disabled:opacity-50"
                >
                  {manualSaving ? t("common.saving", "Se salvează...") : t("clubAthletes.add_athlete", "Adaugă Sportiv")}
                </button>
              ) : (
                <button
                  type="submit"
                  form="club-invite-athlete-form"
                  disabled={
                    inviteLoading ||
                    !inviteEmail.trim() ||
                    !inviteTeamId ||
                    (inviteTarget === "parent" && !inviteAthleteName.trim())
                  }
                  className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium text-sm disabled:opacity-50"
                >
                  {inviteLoading ? t("common.sending", "Se trimite...") : t("invite.send", "Trimite invitație")}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit-athlete modal — full_name/email/phone + team/age_group */}
      {editingAthlete && (
        <div
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4 bg-black/50"
          onClick={() => setEditingAthlete(null)}
        >
          <div
            className="w-full max-w-lg bg-white rounded-2xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex-shrink-0 flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <h3 className="text-lg font-bold text-slate-800">
                {t("clubAthletes.editAthleteTitle", "Editează sportiv")}
              </h3>
              <div className="flex items-center gap-2">
                {/* The two things an admin wants about one athlete are their
                    details and what they pay; from here, the second is one
                    click away instead of a return trip to the table. */}
                <button
                  type="button"
                  onClick={() => {
                    const athlete = editingAthlete;
                    setEditingAthlete(null);
                    if (athlete) openAthleteModal(athlete);
                  }}
                  className="px-3 py-1.5 text-sm font-medium transition-colors rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                >
                  {t("clubAthletes.services", "Servicii")}
                </button>
                <button onClick={() => setEditingAthlete(null)} className="p-2 -mr-2 rounded-lg text-slate-500 hover:bg-slate-100">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                </button>
              </div>
            </div>

            <form id="edit-athlete-form" onSubmit={handleSaveAthleteEdit} className="flex-1 p-5 space-y-4 overflow-y-auto">
              {editAthleteError && (
                <div className="p-3 bg-red-50 text-red-700 rounded-xl text-sm">{editAthleteError}</div>
              )}

              <div>
                <label className="block mb-2 text-sm font-medium text-slate-700">
                  {t("clubAthletes.full_name", "Nume complet")} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editAthleteForm.full_name}
                  onChange={(e) => setEditAthleteForm({ ...editAthleteForm, full_name: e.target.value })}
                  className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium text-slate-700">
                  {t("common.email", "Email")} <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={editAthleteForm.email}
                  onChange={(e) => setEditAthleteForm({ ...editAthleteForm, email: e.target.value })}
                  className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium text-slate-700">{t("clubAthletes.phone", "Telefon")}</label>
                <input
                  type="tel"
                  value={editAthleteForm.phone}
                  onChange={(e) => setEditAthleteForm({ ...editAthleteForm, phone: e.target.value })}
                  className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium text-slate-700">
                  {t("clubAthletes.teams", "Echipe")}
                </label>
                <p className="text-xs text-slate-400 mb-2">
                  {t("clubAthletes.teams_hint", "Un sportiv poate face parte din mai multe echipe.")}
                </p>
                <div className="max-h-44 overflow-y-auto rounded-lg border border-slate-300 divide-y divide-slate-100">
                  {teams.length === 0 && (
                    <p className="px-3 py-2 text-sm text-slate-400">{t("clubAthletes.no_teams", "Nu există echipe")}</p>
                  )}
                  {teams.map((team) => {
                    const checked = editAthleteForm.team_ids.includes(team.id);
                    return (
                      <label key={team.id} className="flex items-center gap-2 px-3 py-2 text-sm text-slate-700 cursor-pointer hover:bg-slate-50">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            setEditAthleteForm((prev) => ({
                              ...prev,
                              team_ids: e.target.checked
                                ? [...prev.team_ids, team.id]
                                : prev.team_ids.filter((id) => id !== team.id),
                            }));
                          }}
                          className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span>{team.name}</span>
                      </label>
                    );
                  })}
                </div>
                {editAthleteForm.team_ids.length === 0 && (
                  <p className="mt-1.5 text-xs text-slate-400">{t("clubAthletes.no_team", "Fără echipă")}</p>
                )}
              </div>

              {/* Fără selector de grupă de vârstă — o dă echipa din care face parte. */}
            </form>

            <div className="flex-shrink-0 flex items-center justify-end gap-3 px-5 py-4 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setEditingAthlete(null)}
                className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium text-sm"
              >
                {t("common.cancel", "Anulează")}
              </button>
              <button
                type="submit"
                form="edit-athlete-form"
                disabled={editAthleteSaving}
                className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium text-sm disabled:opacity-50"
              >
                {editAthleteSaving ? t("common.saving", "Se salvează...") : t("common.save", "Salvează")}
              </button>
            </div>
          </div>
        </div>
      )}
    </ClubAdminLayout>
  );
}
